import { Box, Chip, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { useEffect, useMemo } from 'react'
import {
  canUseBasicOrganizationWithReportType,
  CRASH_LIST_DATASET_FILTER_MERGED,
  getAccessibleStatsBasicOrganizationOptions,
} from '../../pages/Statistics/statistics-dataset-context'
import {
  canUserChangeStatsReportType,
  getAccessibleStatsReportTypes,
} from '../../pages/Statistics/statistics-report-type'
import {
  STATS_TOOLBAR_FORM_CONTROL_SX,
  getStatsPanelSelectSx,
} from './stats-dataset-control-styles'

/**
 * Controlli dataset condivisi (Report Type + categoria dati).
 * @param {'default' | 'toolbar' | 'panel' | 'form'} [variant]
 *   - `panel`: riga compatta con etichetta esterna (Dashboard)
 *   - `form`: FormControl + InputLabel come Form/Field (Custom chart)
 *   - `toolbar`: select su barra DataGrid scura (Crash list)
 */
export function StatsDatasetControls({
  user,
  organizations = [],
  reportType,
  basicOrganization = '',
  onReportTypeChange,
  onBasicOrganizationChange,
  isDark,
  theme,
  selectSx,
  showReportType = true,
  showBasicOrganization = true,
  includeMergedOption = false,
  variant = 'default',
  loading = false,
  /** Se true, Report Type non modificabile (es. analyst ministero in Dashboard). */
  lockReportType = false,
  /** Se true, categoria dati bloccata su Merged (es. analyst in Crash List). */
  lockBasicOrganizationToMerged = false,
}) {
  const { _ } = useLingui()

  const reportTypeItems = useMemo(
    () =>
      getAccessibleStatsReportTypes({
        user,
        organizations,
        mergedLabel: _(msg`Merged`),
      }),
    [user, organizations, _],
  )

  const basicOrgItems = useMemo(
    () =>
      getAccessibleStatsBasicOrganizationOptions(
        {
          allCategories: _(msg`All categories`),
          merged: _(msg`Merged`),
          police: _(msg`Police`),
          hospital: _(msg`Hospital`),
          insurance: _(msg`Insurance`),
        },
        { user, organizations, includeMerged: includeMergedOption },
      ),
    [_, includeMergedOption, user, organizations],
  )

  useEffect(() => {
    if (
      !showBasicOrganization ||
      basicOrganization == null ||
      lockBasicOrganizationToMerged
    ) {
      return
    }
    const allowed = new Set(basicOrgItems.map((opt) => String(opt.id)))
    if (!allowed.has(String(basicOrganization))) {
      onBasicOrganizationChange?.('')
    }
  }, [
    basicOrgItems,
    basicOrganization,
    showBasicOrganization,
    onBasicOrganizationChange,
    lockBasicOrganizationToMerged,
  ])

  const canChangeReportType =
    !lockReportType && canUserChangeStatsReportType(user, organizations)
  const basicOrgDisabled =
    lockBasicOrganizationToMerged ||
    !canUseBasicOrganizationWithReportType(Number(reportType))
  const resolvedBasicOrganization = lockBasicOrganizationToMerged
    ? CRASH_LIST_DATASET_FILTER_MERGED
    : (basicOrganization ?? '')
  const selectedReportLabel =
    reportTypeItems.find((opt) => opt.id === Number(reportType))?.label ??
    reportTypeItems[0]?.label ??
    ''

  const isToolbar = variant === 'toolbar'
  const isPanel = variant === 'panel'
  const isForm = variant === 'form'
  const formSize = isToolbar || isPanel ? 'small' : 'medium'
  const formMargin = isToolbar || isPanel ? 'none' : 'dense'

  const formSelectSx = useMemo(() => {
    const base = selectSx ? selectSx(theme, isDark) : getStatsPanelSelectSx(theme, isDark)
    return {
      ...base,
      '&:hover': { backgroundColor: 'inherit' },
    }
  }, [selectSx, theme, isDark])

  const panelSelectSx = useMemo(() => {
    if (selectSx) return selectSx(theme, isDark)
    return getStatsPanelSelectSx(theme, isDark)
  }, [selectSx, theme, isDark])

  if (!showReportType && !showBasicOrganization) return null

  const handleReportTypeChange = (event) => {
    const next = Number(event.target.value)
    onReportTypeChange?.(next)
    if (!canUseBasicOrganizationWithReportType(next)) {
      onBasicOrganizationChange?.('')
    }
  }

  const renderReportTypeSelect = () => (
    <Select
      id="stats-dataset-report-type"
      value={reportType != null && reportType !== '' ? Number(reportType) : ''}
      onChange={handleReportTypeChange}
      disabled={!canChangeReportType || loading}
      size={formSize}
      sx={isToolbar ? undefined : panelSelectSx}
      displayEmpty
      renderValue={() => selectedReportLabel}
      inputProps={{ 'aria-label': _(msg`Report Type`) }}
    >
      {reportTypeItems.map((opt) => (
        <MenuItem key={`stats-rt-${opt.id}`} value={opt.id}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  )

  const renderReportType = () => {
    if (isForm) {
      return (
        <FormControl margin="dense" fullWidth>
          <InputLabel id="stats-dataset-report-type-label">{_(msg`Report Type`)}</InputLabel>
          <Select
            labelId="stats-dataset-report-type-label"
            id="stats-dataset-report-type"
            label={_(msg`Report Type`)}
            value={reportType != null && reportType !== '' ? Number(reportType) : ''}
            onChange={handleReportTypeChange}
            disabled={!canChangeReportType || loading}
            fullWidth
            sx={formSelectSx}
          >
            {reportTypeItems.map((opt) => (
              <MenuItem key={`stats-rt-${opt.id}`} value={opt.id}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )
    }

    if (isPanel) {
      return (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: isDark ? 'grey.300' : 'text.secondary',
              whiteSpace: 'nowrap',
            }}
          >
            {_(msg`Report Type`)}
          </Typography>
          {!canChangeReportType ? (
            <Chip
              label={selectedReportLabel}
              size="small"
              sx={{
                height: 32,
                fontWeight: 600,
                borderRadius: '8px',
                bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                color: isDark ? 'grey.100' : 'text.primary',
              }}
            />
          ) : (
            <>
              {renderReportTypeSelect()}
              {loading && (
                <CircularProgress size={18} thickness={5} aria-label={_(msg`Loading`)} />
              )}
            </>
          )}
        </Box>
      )
    }

    return (
      <FormControl
        margin={formMargin}
        fullWidth={!isToolbar}
        size={formSize}
        sx={isToolbar ? STATS_TOOLBAR_FORM_CONTROL_SX : undefined}
      >
        {renderReportTypeSelect()}
      </FormControl>
    )
  }

  const renderBasicOrganization = () => {
    const resolveBasicOrgLabel = (selected) => {
      if (selected === '' || selected == null) {
        return basicOrgItems[0]?.label ?? ''
      }
      const match = basicOrgItems.find((opt) => String(opt.id) === String(selected))
      return match?.label ?? String(selected)
    }

    return (
      <FormControl
        margin={formMargin}
        fullWidth={!isToolbar}
        size={formSize}
        sx={isToolbar ? STATS_TOOLBAR_FORM_CONTROL_SX : undefined}
      >
        <Select
          id="stats-dataset-basic-org"
          value={resolvedBasicOrganization}
          displayEmpty
          renderValue={(selected) => resolveBasicOrgLabel(selected)}
          onChange={(e) => onBasicOrganizationChange?.(e.target.value)}
          disabled={basicOrgDisabled}
          sx={isToolbar ? undefined : panelSelectSx}
          size={formSize}
          inputProps={{ 'aria-label': _(msg`Data category`) }}
        >
          {basicOrgItems.map((opt) => (
            <MenuItem key={`stats-bo-${opt.id}`} value={opt.id}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  if (isForm) {
    return (
      <>
        {showReportType && renderReportType()}
        {showBasicOrganization && renderBasicOrganization()}
      </>
    )
  }

  if (isToolbar) {
    return (
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
        {showReportType && renderReportType()}
        {showBasicOrganization && renderBasicOrganization()}
        {loading && showReportType && (
          <CircularProgress size={18} thickness={5} aria-label={_(msg`Loading`)} />
        )}
      </Box>
    )
  }

  if (isPanel) {
    return (
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {showReportType && renderReportType()}
        {showBasicOrganization && renderBasicOrganization()}
      </Box>
    )
  }

  return (
    <Grid container spacing={2}>
      {showReportType && (
        <Grid item xs={12} sm={showBasicOrganization ? 6 : 12} md={showBasicOrganization ? 6 : 4}>
          {renderReportType()}
        </Grid>
      )}
      {showBasicOrganization && (
        <Grid item xs={12} sm={6}>
          {renderBasicOrganization()}
        </Grid>
      )}
    </Grid>
  )
}
